// ResetPassword.tsx
import { UnifiedHeader } from '@/components/UnifiedHeader';
import { fetchWithAuth } from '@/util/apiClient';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import backdropMain from '@/assets/backdrop-main.png';

// =============================================================================
// RESPONSIVE SIZING SYSTEM
// =============================================================================
// Same sizing system as Login.tsx - all values scale proportionally
// Container uses 25vw as preferred value (reduced from 29vw for better screen proportions)
// Min values = max * 0.667 (scales down to ~318px width at smaller viewports)
// =============================================================================

// Container dimensions - 552:409 aspect ratio (1.35:1)
const CONTAINER_WIDTH = 'clamp(318px, 25vw, 480px)';
// Height = width * (409/552) = width * 0.741
const CONTAINER_HEIGHT = 'clamp(236px, calc(25vw * 0.741), 356px)';

// Horizontal padding: (552 - 476) / 2 = 38px each side
const PADDING_X = 'clamp(21px, calc(25vw * 0.069), 33px)'; // 38/552 = 6.9%
// Vertical padding
const PADDING_TOP = 'clamp(17px, calc(25vw * 0.054), 26px)';
const PADDING_BOTTOM = 'clamp(14px, calc(25vw * 0.045), 22px)';

// Font sizes
const TITLE_FONT_SIZE = 'clamp(21px, calc(25vw * 0.065), 31px)'; // Same as Login
const BUTTON_FONT_SIZE = 'clamp(14px, calc(25vw * 0.032), 15px)'; // Increased min from 10px to 14px

// Element heights
const SPACER_HEIGHT = 'clamp(29px, calc(25vw * 0.091), 44px)'; // Same as error area in Login
const INPUT_HEIGHT = 'clamp(30px, calc(25vw * 0.094), 45px)'; // 52px in Figma
const BUTTON_HEIGHT = 'clamp(22px, calc(25vw * 0.069), 33px)'; // 38px in Figma

// Margins
const MARGIN_SM = 'clamp(4px, calc(25vw * 0.013), 6px)';

export default function ResetPasswordPage() {
  const [email, setEmail] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#003D36',
      borderRadius: 0,
      fontFamily: '"Inter", sans-serif',
      '& fieldset': {
        border: 'none',
        borderBottom: '2px solid #7BBCB3',
      },
      '&:hover fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
      '&.Mui-focused fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      '&::placeholder': {
        color: '#7BBCB3',
        opacity: 1,
        fontWeight: 600,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#7BBCB3',
      fontFamily: '"Inter", sans-serif',
      '&.Mui-focused': {
        color: '#7BBCB3',
      },
    },
  };

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSending(true);

    try {
      const response = await fetchWithAuth('/auth/password-reset/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail || 'Failed to send reset email. Please try again.',
        );
      }

      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred. Please try again.',
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Fixed background image */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backdropMain})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
        }}
      />
      <UnifiedHeader
        hideProfileButton
        contactOpen={contactOpen}
        onContactToggle={() => setContactOpen(!contactOpen)}
      />
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            maxWidth: 'calc(100vw - 96px)',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(14, 70, 63, 1)',
              borderRadius: 2,
              paddingLeft: PADDING_X,
              paddingRight: PADDING_X,
              paddingTop: PADDING_TOP,
              paddingBottom: PADDING_BOTTOM,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              height: CONTAINER_HEIGHT,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            {success ? (
              <>
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontFamily: '"Futura PT", "Futura", sans-serif',
                    fontWeight: 700,
                    fontSize: TITLE_FONT_SIZE,
                    marginBottom: MARGIN_SM,
                  }}
                >
                  Check your email
                </Typography>

                <Box sx={{ height: SPACER_HEIGHT }} />

                <Typography
                  sx={{
                    color: '#7BBCB3',
                    fontFamily: '"Inter", sans-serif',
                    fontSize: 'clamp(12px, calc(25vw * 0.029), 14px)',
                    marginBottom: MARGIN_SM,
                  }}
                >
                  If an account exists for {email}, you will receive password
                  reset instructions.
                </Typography>

                <Box sx={{ marginTop: 'auto' }}>
                  <Button
                    fullWidth
                    variant='contained'
                    component={Link}
                    to='/login'
                    disableRipple
                    sx={{
                      backgroundColor: 'rgba(9, 126, 113, 1) !important',
                      color: '#ffffff !important',
                      fontWeight: 600,
                      fontSize: BUTTON_FONT_SIZE,
                      height: BUTTON_HEIGHT,
                      borderRadius: 2,
                      textTransform: 'none',
                      textDecoration: 'none',
                      boxShadow: 'none',
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: '#0a9485 !important',
                        boxShadow: 'none',
                      },
                      '&:active': {
                        backgroundColor: '#065a52 !important',
                      },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontFamily: '"Futura PT", "Futura", sans-serif',
                    fontWeight: 700,
                    fontSize: TITLE_FONT_SIZE,
                    marginBottom: MARGIN_SM,
                  }}
                >
                  Enter your email to reset password
                </Typography>

                <Box
                  sx={{
                    height: SPACER_HEIGHT,
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  {error && (
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: 'clamp(10px, calc(25vw * 0.025), 12px)',
                        fontWeight: 600,
                        color: 'rgba(230, 174, 174, 1)',
                      }}
                    >
                      *{error}
                    </Typography>
                  )}
                </Box>

                <Box
                  component={'form'}
                  onSubmit={handleResetPassword}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    flex: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder='Email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={sending}
                    slotProps={{
                      input: {
                        autoComplete: 'email',
                      },
                    }}
                    sx={{
                      ...inputStyles,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyles['& .MuiOutlinedInput-root'],
                        height: INPUT_HEIGHT,
                        minHeight: INPUT_HEIGHT,
                      },
                    }}
                  />

                  <Box sx={{ marginTop: 'auto' }}>
                    <Button
                      fullWidth
                      variant='contained'
                      type='submit'
                      disabled={sending}
                      disableRipple
                      sx={{
                        backgroundColor: 'rgba(9, 126, 113, 1) !important',
                        color: '#ffffff !important',
                        fontWeight: 600,
                        fontSize: BUTTON_FONT_SIZE,
                        height: BUTTON_HEIGHT,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: 'none',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: '#0a9485 !important',
                          boxShadow: 'none',
                        },
                        '&:active': {
                          backgroundColor: '#065a52 !important',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#4a7d78 !important',
                          color: 'rgba(255, 255, 255, 0.4) !important',
                        },
                      }}
                    >
                      {sending ? 'Sending...' : 'Reset Password'}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
